"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2 } from "lucide-react";
import { registerStudent } from "@/actions/registration";
import { AccountConnectedCard } from "../account-connected-card";
import { cn } from "@/lib/utils";

interface AuthenticatedUser {
  id: string;
  email: string;
  name: string | null;
}

interface StudentRegistrationFormProps {
  authenticatedUser?: AuthenticatedUser | null;
}

const studentSchema = z.object({
  email: z.string().email("Invalid email address"),
  fullName: z.string().min(2, "Name must be at least 2 characters"),
  phone: z.string().optional(),
  schoolName: z.string().min(2, "School name is required"),
  course: z.string().optional(),
  level: z.string().optional(),
});

type StudentFormData = z.infer<typeof studentSchema>;

export function StudentRegistrationForm({
  authenticatedUser,
}: StudentRegistrationFormProps) {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const form = useForm<StudentFormData>({
    resolver: zodResolver(studentSchema),
    defaultValues: {
      email: authenticatedUser?.email || "",
      fullName: authenticatedUser?.name || "",
      schoolName: "",
    },
  });

  const onSubmit = async (data: StudentFormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const submitData = {
        email: data.email || "",
        fullName: data.fullName || "",
        phone: data.phone,
        schoolName: data.schoolName || "",
        course: data.course,
        level: data.level,
      };

      const result = await registerStudent(submitData, authenticatedUser?.id);

      if (result.success) {
        if (authenticatedUser) {
          router.push("/success?type=student&verified=true");
        } else {
          router.push(
            `/verification-pending?email=${encodeURIComponent(submitData.email)}&type=student`,
          );
        }
      } else {
        setErrorMessage(
          result.message ||
            "Registration failed. Please check your details and try again.",
        );
      }
    } catch (error) {
      console.error("Registration error:", error);
      setErrorMessage(
        "An unexpected error occurred. Please try again later.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto">
      <AccountConnectedCard user={authenticatedUser} />
      {errorMessage && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{errorMessage}</p>
        </div>
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  Email Address
                  {authenticatedUser && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                      <CheckCircle2 className="h-3 w-3" />
                      Verified
                    </span>
                  )}
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    placeholder="your@email.com"
                    disabled={!!authenticatedUser}
                    className={cn(
                      authenticatedUser && "bg-gray-50 cursor-not-allowed",
                    )}
                  />
                </FormControl>
                {authenticatedUser && (
                  <p className="text-xs text-green-600 mt-1">
                    Via Edukate Africa account
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="fullName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Full Name</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="John Doe"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone Number (Optional)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="schoolName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>School/University Name</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="e.g., University of Technology"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="course"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Course/Major (Optional)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="e.g., Computer Science"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="level"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Academic Level (Optional)</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    placeholder="e.g., Year 2, Final Year"
                    disabled={isLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-600 hover:bg-blue-700"
            size="lg"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Registering...
              </>
            ) : (
              "Complete Registration"
            )}
          </Button>

          <p className="text-center text-xs text-gray-600">
            {authenticatedUser
              ? "Your registration will be instantly verified"
              : "We'll send you a verification email to confirm your registration"}
          </p>
        </form>
      </Form>
    </div>
  );
}
